import type { ListDealWithRbo } from '@openx/types/package/PackageHookTypes.ts';

const DISPLAY_LIMIT = 50;

export type DealAction = 'no_action' | 'reset' | 'disable_on_incompatible';
export type DealWithAction = { name: string; id: string; action: DealAction };

const ACTION_PRIORITY: Record<DealAction, number> = {
  disable_on_incompatible: 0,
  reset: 1,
  no_action: 2,
};

export const computeDealsWithActions = (
  deals: ListDealWithRbo[],
  isIncompatibleTargeting: boolean,
  hasInaccessibleDeals: boolean
): {
  dealsWithAction: DealWithAction[];
  allDealsToDisable: string[];
  dealsToReset: string[];
  hasMore: boolean;
} => {
  const allDealsWithAction = deals.map(deal => {
    const rbo = deal.rbo_config;
    const hasRbo = !!rbo;
    const isCustomMode = rbo?.mode === 'custom';
    const isFixedTactic = rbo?.tactic === 'fixed';

    const dealInfo = { name: deal.name, id: deal.id, dealId: deal.deal_id };

    if (!hasRbo) {
      return { ...dealInfo, action: 'no_action' as const };
    }

    if (!isCustomMode) {
      // Non-custom mode: action depends on whether format is incompatible
      const action = isIncompatibleTargeting ? 'disable_on_incompatible' : 'reset';

      return { ...dealInfo, action: action as DealAction };
    }

    // Custom mode: reset if not fixed tactic (never disabled)
    const action = isFixedTactic ? 'no_action' : 'reset';

    return { ...dealInfo, action: action as DealAction };
  });

  const sortedByAction = [...allDealsWithAction].sort((a, b) => ACTION_PRIORITY[a.action] - ACTION_PRIORITY[b.action]);

  return {
    dealsWithAction: sortedByAction.slice(0, DISPLAY_LIMIT),
    allDealsToDisable: sortedByAction
      .filter(deal => deal.action === 'disable_on_incompatible')
      .map(deal => deal.dealId)
      .filter(Boolean) as string[],
    dealsToReset: sortedByAction
      .filter(deal => deal.action === 'reset')
      .map(deal => deal.dealId)
      .filter(Boolean) as string[],
    hasMore: sortedByAction.length > DISPLAY_LIMIT || hasInaccessibleDeals,
  };
};
